import type { ComputedRef, Ref } from 'vue'
import type { FetchContext, FetchResponse } from 'ofetch'
import type {
  EventCalendarDay,
  EventCalendarDayMap,
  EventDate,
} from '~/composables/types/event'

export const useMergedCalendarDayMap = (
  calendarDayMap: Ref<EventCalendarDayMap> | ComputedRef<EventCalendarDayMap>
) => {
  const mergedCalendaryDayMap = ref<EventCalendarDayMap>({
    ...(calendarDayMap?.value || {}),
  })

  watchEffect(() => {
    if (calendarDayMap?.value) {
      Object.keys(calendarDayMap.value).forEach((key) => {
        mergedCalendaryDayMap.value[key] = calendarDayMap.value[key]
      })
    }
  })

  return mergedCalendaryDayMap
}

export const useFetchedMonths = () => {
  const fetchedMonths = ref<Record<string, boolean>>({})

  const onResponse = (
    context: FetchContext & { response: FetchResponse<EventCalendarDayMap> }
  ): Promise<void> | void => {
    const { response } = context

    if (response?.status !== 200) return
    const responseUrl = new URL(response?.url)
    const searchParams = responseUrl.searchParams
    const month = searchParams.get('month')
    if (month) {
      fetchedMonths.value[month] = true
    }
  }

  return {
    onResponse,
    fetchedMonths,
  }
}

export const useDatePickerOverlay = () => {
  const isYearOverlayOpen = ref(false)
  const isMonthOverlayOpen = ref(false)

  const isAnyOverlayOpen = computed(
    () => isYearOverlayOpen.value || isMonthOverlayOpen.value
  )

  const onOverlayToggle = (overlay: { overlay: string; open: boolean }) => {
    const map: Record<string, Ref<boolean>> = {
      month: isMonthOverlayOpen,
      year: isYearOverlayOpen,
    }

    if (map[overlay?.overlay]) {
      map[overlay?.overlay].value = overlay.open
    }
  }

  return {
    onOverlayToggle,
    isYearOverlayOpen,
    isAnyOverlayOpen,
    isMonthOverlayOpen,
  }
}

export const buyBoxDatePickerMarkerColors = {
  PUBLIC: 'rgb(134, 163, 21)',
  PRIVATE: 'rgb(2, 97, 102)',
  red: '#A42515',
}

export const useCalendarCellMarkers = (
  calendarDayMap: ComputedRef<EventCalendarDayMap> | Ref<EventCalendarDayMap>,
  selectedTicketsQuantity: ComputedRef<number> | Ref<number>
) => {
  const { t } = useI18n()

  const lightColors = {
    PUBLIC: 'rgb(134, 163, 21, 0.5)',
    PRIVATE: 'rgb(2, 97, 102, 0.5)',
  }

  const getDateColorsByDateType = (
    dates: EventDate[],
    dateType: 'PUBLIC' | 'PRIVATE'
  ) => {
    const datesFilteredByType = dates.filter((date) =>
      dateType === 'PUBLIC'
        ? eventDateGetters.getIsPublic(date)
        : eventDateGetters.getIsPrivate(date)
    )

    if (datesFilteredByType.length === 0) return undefined

    const maxTickets = Math.max(
      ...datesFilteredByType.map((dateItem) =>
        eventDateGetters.getAvailableTicketsQuantity(dateItem)
      )
    )

    if (selectedTicketsQuantity.value <= maxTickets) {
      return buyBoxDatePickerMarkerColors[dateType]
    } else {
      return lightColors[dateType]
    }
  }

  const getColorByCalendarDay = (calendarDay: EventCalendarDay) => {
    if (!calendarDay.tickets_left) {
      return [buyBoxDatePickerMarkerColors.red]
    }

    const publicColors = getDateColorsByDateType(calendarDay.dates, 'PUBLIC')
    const privateColors = getDateColorsByDateType(calendarDay.dates, 'PRIVATE')

    return [publicColors, privateColors].filter((x) => Boolean(x))
  }

  return computed(() => {
    return Object.values(calendarDayMap.value)?.map((calendarDay) => ({
      date: calendarDay.day,
      color: getColorByCalendarDay(calendarDay),
      type: 'line',
      ...(calendarDay?.tickets_left < 8 && {
        tooltip: [
          {
            text:
              calendarDay.tickets_left > 0
                ? t(
                    'product.show.checkout.onlyNTicketsLeft',
                    calendarDay?.tickets_left
                  )
                : t('product.status.soldOut'),
            color: getColorByCalendarDay(calendarDay),
          },
        ],
      }),
    }))
  })
}
